import { Typography } from '@eye-able/ui';
import { cn } from '@/helpers/cn';
import { type ReactNode, useLayoutEffect } from 'react';
import {
  type TranslationNode,
  useInlineTranslation,
} from '@/i18n/hooks/useInlineTranslation';

interface PageTitleProps {
  actionsSlot?: ReactNode;
  description?: TranslationNode | string;
  heading: TranslationNode;
  icon?: ReactNode;
  subheading?: TranslationNode;
}

const PageTitle = ({
  actionsSlot,
  description,
  heading,
  icon,
  subheading,
}: PageTitleProps) => {
  const { text } = useInlineTranslation();
  const variant = icon ? 'headline-l' : 'headline-xl';

  useLayoutEffect(() => {
    const section = subheading ? ` - ${text(subheading)}` : '';

    document.title = `${text(heading)}${section} | Eye-Able Dashboard`;
  }, [text, heading, subheading]);

  return (
    <section
      data-test-id='page-title'
      className='eye:w-full eye:pb-xs eye:lg:pb-s'
    >
      <div className='eye:flex eye:items-start eye:gap-xs eye:justify-between eye:max-lg:flex-col-reverse'>
        <div
          className='eye:flex eye:flex-row eye:max-w-full'
          style={{ minWidth: 0 }}
        >
          {icon && <div className='eye:mr-2xs'>{icon}</div>}
          <div className='eye:overflow-hidden'>
            <Typography tag='h1' variant={variant}>
              {heading}
              {subheading ? (
                <Typography
                  tag='span'
                  variant={variant}
                  noWrap
                  className='eye:block eye:font-regular!'
                  title={text(subheading) ?? ''}
                >
                  {subheading}
                </Typography>
              ) : null}
            </Typography>
          </div>
        </div>
        {actionsSlot && (
          <div
            className={cn(
              'eye:flex eye:max-sm:flex-col eye:flex-row eye:gap-xs eye:items-center eye:max-lg:self-end eye:max-sm:w-full',
            )}
          >
            {actionsSlot}
          </div>
        )}
      </div>
      {description ? (
        <Typography
          tag='div'
          variant='body-m'
          className={cn('eye:mt-5xs eye:w-full', icon && 'eye:ml-l')}
        >
          {description}
        </Typography>
      ) : null}
    </section>
  );
};

export default PageTitle;
